import * as React from "react";
import { Controller } from "react-hook-form";

import { SKILL_DESCRIPTION_MAX_LENGTH } from "@/shared/domain/skills";
import { Field, FormSection, Textarea } from "@/shared/foundation/primitives";

import {
  PROCEDURE_HINT,
  PROCEDURE_LABEL,
  WHEN_TO_USE_LABEL,
  whenToUseEditHint,
} from "./skillFieldCopy";
import styles from "./SkillVersionFields.module.css";
import type { SkillVersionForm } from "./skillVersionForm";
import { TopicBodyEditor } from "./TopicBodyEditor";

interface Props {
  form: SkillVersionForm;
  orgId: string | undefined;
}

/**
 * The two editable fields of a skill version — "when to use" and the Markdown
 * procedure — plus the save error, if the last attempt failed.
 *
 * Each field sits in the same {@link FormSection} card the reader sees,
 * so switching modes swaps the control inside a block rather than replacing one
 * page layout with a different-looking one. That's why neither input renders a
 * label of its own: the block heading is the label, and both point at it with
 * `aria-labelledby` instead of stating the field name twice. Their error slot
 * is likewise reserved for validation messages — the standing hint belongs to
 * the block header, where read mode shows it too.
 *
 * Presentational: field state, validation, and save handling live in
 * {@link SkillVersionForm}, so every surface that edits a version renders
 * identical fields over identical semantics.
 *
 * Both fields go through `Controller` rather than `register`: the body editor
 * is a controlled component with its own `value` / `onChange` contract, and
 * binding them the same way keeps the two from needing different rules.
 */
export function SkillVersionFields({ form, orgId }: Props) {
  const descriptionLabelId = React.useId();
  const bodyLabelId = React.useId();
  return (
    <>
      <Controller
        name="description"
        control={form.control}
        render={({ field, fieldState }) => (
          <FormSection
            title={WHEN_TO_USE_LABEL}
            hint={whenToUseEditHint()}
            titleId={descriptionLabelId}
          >
            <Field.Root name={field.name} invalid={fieldState.invalid}>
              <Field.Control
                render={<Textarea rows={2} />}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                maxLength={SKILL_DESCRIPTION_MAX_LENGTH}
                aria-labelledby={descriptionLabelId}
                required
              />
              <Field.Error match>{fieldState.error?.message}</Field.Error>
            </Field.Root>
          </FormSection>
        )}
      />
      <Controller
        name="body"
        control={form.control}
        render={({ field, fieldState }) => (
          <FormSection
            title={PROCEDURE_LABEL}
            hint={PROCEDURE_HINT}
            titleId={bodyLabelId}
            className={styles.procedure}
          >
            <TopicBodyEditor
              labelledBy={bodyLabelId}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              topicNames={form.topicNames}
              orgId={orgId}
              error={fieldState.error !== undefined}
              helperText={fieldState.error?.message}
              inputClassName={styles.bodyInput}
              grow
              required
            />
          </FormSection>
        )}
      />
      {form.errorMessage && (
        <small className={styles.errorMessage}>{form.errorMessage}</small>
      )}
    </>
  );
}
